import { useMemo } from 'react';

import type { DropDownItem } from '@eye-able/ui';

interface UseLimitedComboboxItemsParams<T extends DropDownItem> {
  items: T[];
  searchValue: string;
  selectedValue?: string;
}

// Rendering very large option lists hurts combobox usability and can cause browser-side
// rendering/interaction lag, so we cap visible search results to a manageable amount.
const MAX_RENDERED_SEARCH_RESULTS = 99;

export function useLimitedComboboxItems<T extends DropDownItem>({
  items,
  searchValue,
  selectedValue,
}: UseLimitedComboboxItemsParams<T>) {
  const requiresTypeAhead = items.length > MAX_RENDERED_SEARCH_RESULTS;

  const comboboxItems = useMemo(() => {
    if (!items.length) return [];

    const normalizedSearch = searchValue.trim().toLowerCase();

    if (!requiresTypeAhead) {
      if (!normalizedSearch) return items;
      return items.filter((item) =>
        item.label.toLowerCase().includes(normalizedSearch),
      );
    }

    if (!normalizedSearch && selectedValue) {
      const selectedItem =
        items.find((item) => item.value === selectedValue) ?? null;

      // keep the selected option visible even when type-ahead mode is active
      if (selectedItem) return [selectedItem];
    }

    return items
      .filter((item) => item.label.toLowerCase().includes(normalizedSearch))
      .slice(0, MAX_RENDERED_SEARCH_RESULTS);
  }, [items, requiresTypeAhead, searchValue, selectedValue]);

  return { comboboxItems, requiresTypeAhead };
}
